import { dataUrlToFile } from '../config/capacitor';

const MAX_SOURCE_FILE_BYTES = 50 * 1024 * 1024;
const MAX_PROCESSED_FILE_BYTES = 12 * 1024 * 1024;
const UPLOAD_PRIMARY_MAX_EDGE = 2048;
const UPLOAD_FALLBACK_MAX_EDGE = 1600;
const PREVIEW_MAX_EDGE = 768;
const UPLOAD_PRIMARY_QUALITY = 0.9;
const UPLOAD_FALLBACK_QUALITY = 0.82;
const PREVIEW_QUALITY = 0.82;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to decode image data'));
    image.src = dataUrl;
  });
}

function canvasToDataUrl(canvas, quality) {
  return canvas.toDataURL('image/jpeg', quality);
}

function renderToCanvas(image, maxEdge) {
  const longestEdge = Math.max(image.width, image.height) || 1;
  const scale = Math.min(1, maxEdge / longestEdge);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to initialize image canvas');
  }

  context.drawImage(image, 0, 0, width, height);
  return canvas;
}

function inferSourceType(file, dataUrl) {
  if (file?.type) {
    return file.type.toLowerCase();
  }

  const header = String(dataUrl || '').split(',')[0] || '';
  const match = header.match(/data:(.*?);base64/i);
  return match?.[1]?.toLowerCase() || 'image/jpeg';
}

function isHeicLike(file, sourceType) {
  const lowerName = String(file?.name || '').toLowerCase();
  return sourceType.includes('heic') || sourceType.includes('heif') || lowerName.endsWith('.heic') || lowerName.endsWith('.heif');
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function createEmptyImageSlotState(slot) {
  return {
    slot,
    assetId: '',
    file: null,
    previewUrl: '',
    sourceType: '',
    uploadType: '',
    uploadSizeLabel: '',
    originalSizeLabel: '',
    originalSizeBytes: 0,
    source: '',
  };
}

export function restoreImageSlotState(previewDataUrl, filenameBase, slot) {
  if (!previewDataUrl) {
    return createEmptyImageSlotState(slot);
  }

  const file = dataUrlToFile(previewDataUrl, filenameBase);
  return {
    slot,
    assetId: `restored-${slot}-${Date.now()}`,
    file,
    previewUrl: previewDataUrl,
    sourceType: file.type || 'image/jpeg',
    uploadType: file.type || 'image/jpeg',
    uploadSizeLabel: formatBytes(file.size),
    originalSizeLabel: formatBytes(file.size),
    originalSizeBytes: file.size,
    source: 'restored',
  };
}

export async function prepareImageUploadAsset({ file = null, dataUrl = '', filenameBase = 'image', slot = 'A', source = 'file' }) {
  if (file?.size && file.size > MAX_SOURCE_FILE_BYTES) {
    const error = new Error('Selected image is too large. Please use an image under 50 MB.');
    error.code = 'file_too_large';
    throw error;
  }

  const sourceDataUrl = dataUrl || (file ? await readFileAsDataUrl(file) : '');
  if (!sourceDataUrl) {
    const error = new Error('No image data was found.');
    error.code = 'empty_image';
    throw error;
  }

  let image;
  try {
    image = await loadImageFromDataUrl(sourceDataUrl);
  } catch (decodeError) {
    const error = new Error('This image format could not be processed. Try a JPEG or PNG image.');
    error.code = 'image_decode_failed';
    error.cause = decodeError;
    throw error;
  }

  const sourceType = inferSourceType(file, sourceDataUrl);
  const hadHeicSource = isHeicLike(file, sourceType);

  const primaryCanvas = renderToCanvas(image, UPLOAD_PRIMARY_MAX_EDGE);
  let uploadDataUrl = canvasToDataUrl(primaryCanvas, UPLOAD_PRIMARY_QUALITY);
  let uploadFile = dataUrlToFile(uploadDataUrl, filenameBase);

  if (uploadFile.size > MAX_PROCESSED_FILE_BYTES) {
    const fallbackCanvas = renderToCanvas(image, UPLOAD_FALLBACK_MAX_EDGE);
    uploadDataUrl = canvasToDataUrl(fallbackCanvas, UPLOAD_FALLBACK_QUALITY);
    uploadFile = dataUrlToFile(uploadDataUrl, filenameBase);
  }

  if (uploadFile.size > MAX_PROCESSED_FILE_BYTES) {
    const error = new Error('The processed image is still too large to upload safely. Try a smaller image.');
    error.code = 'processed_file_too_large';
    throw error;
  }

  const previewCanvas = renderToCanvas(image, PREVIEW_MAX_EDGE);
  const previewUrl = canvasToDataUrl(previewCanvas, PREVIEW_QUALITY);

  return {
    slot,
    assetId: `${slot}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    file: uploadFile,
    previewUrl,
    sourceType,
    uploadType: uploadFile.type || 'image/jpeg',
    uploadSizeLabel: formatBytes(uploadFile.size),
    originalSizeLabel: formatBytes(file?.size || uploadFile.size),
    originalSizeBytes: file?.size || uploadFile.size,
    source,
    didNormalizeToJpeg: true,
    hadHeicSource,
  };
}

export function getImageInputErrorMessage(error) {
  const code = error?.code || '';

  if (code === 'permission_denied') {
    return 'Camera or photo library access was denied. Check app permissions and try again.';
  }

  if (code === 'cancelled') {
    return '';
  }

  if (code === 'file_too_large') {
    return error.message;
  }

  if (code === 'processed_file_too_large') {
    return error.message;
  }

  if (code === 'image_decode_failed') {
    return 'This image format could not be converted. Try JPEG, PNG, or capture a new photo.';
  }

  if (code === 'empty_image') {
    return 'No image data was found. Please choose the image again.';
  }

  return 'The image could not be prepared. Please try another photo.';
}
